import request from '@/utils/request';
import {
  OrderItem,
  OrderOperationResponse,
  OrderQueryParams,
  OrderResponse,
  OrderStatistics,
  OrderStatus,
  UpdateRecipientInfo,
} from '@/types/order';

export const orderService = {
  getOrderList: async (params: OrderQueryParams): Promise<OrderResponse> => {
    const response = await request.get<OrderResponse>('/api/admin/orders/search', { params });
    return response;
  },

  getOrderById: async (id: string): Promise<OrderItem> => {
    const response = await request.get<OrderItem>(`/api/admin/orders/detail`, { params: { orderNo: id } });
    return response;
  },


  updateOrderStatus: async (id: number, status: OrderStatus, remarks?: string): Promise<OrderItem> => {
    const response = await request.put<OrderItem>(`/api/admin/orders/${id}/status`, { status, remarks });
    return response;
  },

  getOrderStatistics: async (startTime?: string, endTime?: string): Promise<OrderStatistics> => {
    const response = await request.get<OrderStatistics>('/api/admin/orders/statistics', {
      params: { startTime, endTime },
    });
    return response;
  },

  getOrderOperations: async (orderId: number): Promise<OrderOperationResponse> => {
    const response = await request.get<OrderOperationResponse>(`/api/admin/orders/${orderId}/operations`);
    return response;
  },

  cancelOrder: async (
    orderNo: string,
    celCode: string,
    celReason: string,
    addToBlacklist?: boolean
  ): Promise<OrderOperationResponse> => {
    const body: Record<string, unknown> = { orderNo, celCode, celReason };
    if (addToBlacklist) {
      body.addToBlacklist = true;
    }
    const response = await request.post(`/api/admin/orders/cancel`, body);
    return response;
  },

  /** 一键截单（取消中/待审核/待发货） */
  interceptOrder: async (orderNo: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/intercept`, { orderNo });
    return response;
  },

  /** 管理员起租：待使用 -> 租用中 */
  startRental: async (orderNo: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/start-rental`, { orderNo });
    return response;
  },

  updateOrderRemark: async (orderNo: string, remark: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/update-order-remark`, { orderNo, remark });
    return response;
  },

  updateOrderPrice: async (orderNo: string, promotionDiscountAmount: number): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/update-order-price`, { orderNo, promotionDiscountAmount });
    return response;
  },
  updateRecipientInfo: async (params: UpdateRecipientInfo) => {
    const response = await request.post(`/api/admin/orders/update-recipient-info`, params);
    return response;
  },

  skipAssessment: async (orderNo: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/skip-assessment`, { orderNo });
    return response;
  },
  approveOrder: async (orderNo: string, reviewReason: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/approve-order`, { orderNo, reviewReason });
    return response;
  },
  repDeposit: async (orderNo: string, replenishDeposit: number, settlementId: number | null): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/rep-deposit`, { orderNo, replenishDeposit, settlementId });
    return response;
  },

  updateImeiOrSn: async (orderNo: string, serialNumber: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/update-imei-or-sn`, { orderNo, serialNumber });
    return response;
  },
  settlementOrder: async (orderNo: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/settlementToConsumer`, { orderNo });
    return response;
  },
  completeOrder: async (orderNo: string, fulfillmentFinishStatus: string): Promise<OrderOperationResponse> => {
    const response = await request.post(`/api/admin/orders/finish-order`, { orderNo, fulfillmentFinishStatus });
    return response;
  },

  renewOrder: async (orderNo: string, leaseRenewStart: string, leaseRenewEnd: string, activeRenewalFlag: boolean, actualRentAmount: number) => {
    const response = await request.post(`/api/admin/orders/renew`, {
      orderNo,
      leaseRenewStart,
      leaseRenewEnd,
      activeRenewalFlag,
      actualRentAmount,
    });
    return response;
  },
  overdueRenewOrder: async (orderId: number, overdueAmount?: number) => {
    const response = await request.get(`/api/admin/orders/overdue-renew`, { params:{orderId, overdueAmount}});
    return response;
  },
  fetchRiskInfo: async (userId: number) => {
    const response = await request.get(`/api/admin/risk-control-reports/fetch-risk-info/${userId}`);
    return response;
  },

  cancelCancelOrder: async (orderNo: string) => {
    const response = await request.get(`/api/admin/orders/${orderNo}/cancel-cancel`);
    return response;
  },

  // 查询买断订单列表
  getBuyoutOrderList: async (params: any): Promise<any> => {
    const response = await request.get('/api/admin/orders/buyout/search', { params });
    return response;
  },

  // 取消买断订单
  cancelBuyoutOrder: async (
    orderNo: string,
    settlementId: number,
    celCode: string,
    celReason: string
  ): Promise<any> => {
    const response = await request.post('/api/admin/orders/buyout/cancel', {
      orderNo,
      settlementId,
      celCode,
      celReason,
    });
    return response;
  },

  // 创建追加增值服务费账单
  createExtraValueAddedSettlement: async (params: {
    orderId: number;
    amount: number;
    extraValueAddedType: string;
    remark?: string;
  }): Promise<any> => {
    const response = await request.post('/api/admin/orders/create-extra-value-added-settlement', params);
    return response;
  },

  // 确认租金账单线下支付（含首期/多期及续租租金）
  confirmOfflineRentPayment: async (params: {
    settlementId: number;
    actualAmount: number;
    actualPaymentTime: string;
    outTradeNo?: string;
    remark?: string;
  }): Promise<any> => {
    const response = await request.post('/api/admin/orders/confirm-offline-rent-payment', params);
    return response;
  },

  // 取消账单支付
  cancelSettlementPayment: async (settlementId: number): Promise<any> => {
    const response = await request.post('/api/admin/orders/cancel-settlement-payment', { settlementId });
    return response;
  },

  // 商户转单
  transferOrderMerchant: async (orderNo: string, targetMerchantId: number): Promise<boolean> => {
    const response = await request.post('/api/admin/orders/transfer-merchant', {
      orderNo,
      targetMerchantId,
    });
    return response;
  },

  downloadShipmentScreenTemplate: async (): Promise<void> => {
    const response = await request.get('/api/admin/orders/shipping-screen/template', {
      responseType: 'blob',
    });
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '发货筛查导入模板.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  importShipmentScreen: async (formData: FormData): Promise<void> => {
    const response = await request.post('/api/admin/orders/shipping-screen/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `发货筛查结果_${new Date().getTime()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  exportOrders: async (params: OrderQueryParams): Promise<void> => {
    const response = await request.get<Blob>('/api/admin/orders/export', {
      params,
      responseType: 'blob',
    });

    const blob = response instanceof Blob ? response : new Blob([response]);
    if (blob.type.includes('json')) {
      const text = await blob.text();
      let errorMessage = '导出失败';
      try {
        const json = JSON.parse(text) as { message?: string };
        if (json.message) {
          errorMessage = json.message;
        }
      } catch {
        // 非 JSON 响应，使用默认文案
      }
      throw new Error(errorMessage);
    }

    const downloadBlob = new Blob([blob], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(downloadBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `支付宝线上订单_${new Date().getTime()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
